import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldIcon, TruckIcon, ZapIcon, MapPinIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface AboutPageProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function AboutPage({
  setPage
}: AboutPageProps) {
  return <div className="w-full bg-white" data-id="element-130" data-mp="3icvj2p23e2gx">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-131" data-mp="3de0raxm5d3ko">
        <div className="max-w-7xl mx-auto text-center" data-id="element-132" data-mp="3dflmmpgacqjl">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-4" data-id="element-133" data-mp="1gjxj899we9mh">
            About <span className="text-brand-red" data-id="element-134" data-mp="2eyk6slqvwpsb">Merem Blasting</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg text-steel-300 font-body max-w-2xl mx-auto" data-id="element-135" data-mp="2jt7mbv3wu9ur">
            Michigan's premier mobile surface preparation company. We bring
            industrial-grade blasting directly to your site.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4" data-id="element-136" data-mp="32nmmho1yk8k6">
        <div className="max-w-7xl mx-auto" data-id="element-137" data-mp="2ov6qv8bdzcta">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-138" data-mp="2osauvpj3my7h">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative h-[500px] rounded-sm overflow-hidden shadow-2xl" data-id="element-139" data-mp="2trx83yyxgega">
              <img src="https://cdn.magicpatterns.com/uploads/irFTcRK7C5jYGfaG48nkuy/peter.jpg" alt="Peter Merem, Owner & Operator" className="w-full h-full object-cover" data-id="element-140" data-mp="3m6sj39le0afs" />
              <div className="absolute inset-0 bg-brand-red/10 mix-blend-multiply" data-id="element-141" data-mp="3d887ese8v112"></div>
              <div className="absolute bottom-6 left-6 bg-white p-4 shadow-lg border-l-4 border-brand-red" data-id="element-142" data-mp="38em4hrxvubig">
                <p className="font-heading font-bold uppercase text-charcoal text-xl" data-id="element-143" data-mp="33hdkdlt6bxek">
                  Peter Merem
                </p>
                <p className="text-steel-500 text-sm font-medium" data-id="element-144" data-mp="2tiwnk87k0okl">
                  Owner & Operator
                </p>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-145" data-mp="2pi4epg2lglou">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-6" data-id="element-146" data-mp="192qbt7j62fnx">
                Real Results. No Corporate Fluff.
              </motion.h2>
              <motion.div variants={fadeInUp} className="space-y-6 text-steel-600 leading-relaxed text-lg" data-id="element-147" data-mp="lcm4oq6rrxs6">
                <p data-id="element-148" data-mp="v9qwb5jdwlgd">
                  Merem Mobile Dustless Blasting Michigan, LLC was built on a
                  simple premise: provide fast, reliable, and highly effective
                  surface preparation services directly to the customer's
                  location.
                </p>
                <p data-id="element-149" data-mp="7jmriuugmfns">
                  Based in Fowlerville, Michigan, owner Peter Merem and his team
                  travel across the entire state, bringing state-of-the-art
                  dustless and dry blasting equipment to industrial sites,
                  commercial properties, farms, and residential locations.
                </p>
                <p data-id="element-150" data-mp="2j0g937ul68in">
                  We understand that downtime costs money. Whether you're
                  restoring a classic car, prepping a fleet of heavy machinery,
                  or removing graffiti from a historic building, you need a team
                  that shows up on time, works safely, and leaves a clean,
                  paint-ready surface.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-10" data-id="element-151" data-mp="31wy7g83xx6nv">
                <Button variant="primary" onClick={() => setPage('contact')} data-id="element-152" data-mp="2wzpqubyfxaoq">
                  Work With Us
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-steel-50 px-4 border-y border-steel-200" data-id="element-153" data-mp="12mzrhepaz99y">
        <div className="max-w-7xl mx-auto" data-id="element-154" data-mp="3e8qt8uzedug5">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-155" data-mp="3j7lb9upjjez7">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-156" data-mp="39fpd6jngfouz">
              Our Commitment to the Job
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-157" data-mp="30hdeducfpkrg">
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-100 text-center" data-id="element-158" data-mp="kpplca0jkcdx">
              <div className="w-16 h-16 bg-steel-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-steel-200" data-id="element-159" data-mp="oqhu725i4f9o">
                <ZapIcon className="w-8 h-8 text-brand-red" data-id="element-160" data-mp="1sovayiaxwt9u" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-161" data-mp="1sqzo7quqb4ms">
                Fast Turnaround
              </h3>
              <p className="text-steel-600" data-id="element-162" data-mp="1joqh7ayo5te5">
                We pride ourselves on quick estimates and efficient on-site
                work. We respect your timeline and work diligently to keep your
                project moving forward.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-100 text-center" data-id="element-163" data-mp="27lw4mrcoyyay">
              <div className="w-16 h-16 bg-steel-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-steel-200" data-id="element-164" data-mp="22mjh04dzxn7b">
                <ShieldIcon className="w-8 h-8 text-brand-red" data-id="element-165" data-mp="2vbqdyyxsy4qg" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-166" data-mp="2v9nfrrmhx5an">
                Safety & Compliance
              </h3>
              <p className="text-steel-600" data-id="element-167" data-mp="37l6bkqwwrlx8">
                OSHA-conscious and safety-forward. Our dustless process requires
                less containment, reduces airborne hazards, and is EPA/DEQ
                conscious.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-100 text-center" data-id="element-168" data-mp="3llqstin6d9om">
              <div className="w-16 h-16 bg-steel-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-steel-200" data-id="element-169" data-mp="3qjivn7pcyg7l">
                <TruckIcon className="w-8 h-8 text-brand-red" data-id="element-170" data-mp="3vdwlkul93v4v" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-171" data-mp="44g2u49xyuru">
                Fully Mobile
              </h3>
              <p className="text-steel-600" data-id="element-172" data-mp="d5nui3hycxcy">
                We bring the blast yard to you. Our self-contained mobile units
                have everything needed to complete the job on your site,
                anywhere in Michigan.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Service Area Banner */}
      <section className="py-16 bg-charcoal text-white text-center px-4" data-id="element-173" data-mp="18cz8l1zq49pj">
        <div className="max-w-4xl mx-auto" data-id="element-174" data-mp="13ha36i10vylb">
          <MapPinIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-175" data-mp="fqe7vaxcyl9b" />
          <h2 className="text-3xl font-heading font-extrabold uppercase mb-4" data-id="element-176" data-mp="fqo1d8hm3ajr">
            Based in Fowlerville. Serving All of Michigan.
          </h2>
          <p className="text-steel-300 text-lg mb-8" data-id="element-177" data-mp="pgfurzdz5xmn">
            From Detroit to Grand Rapids, Lansing to Traverse City, we travel
            state-wide to handle your surface preparation needs.
          </p>
          <Button variant="primary" onClick={() => setPage('contact')} data-id="element-178" data-mp="3t496o8hajs0s">
            Request an On-Site Estimate
          </Button>
        </div>
      </section>
    </div>;
}